import React, { useCallback, useMemo } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { pick } from 'ramda';

import * as Yup from 'yup';
import FmcForm from 'components/FmcForm';
import BillingAddressView from './BillingAddressView';

import useTranslate from 'hooks/useTranslate';

import { selectTenant } from 'state/tenant';
import {
  mergeApplication,
  selectCreditApplication,
} from 'state/creditApplication';
import {
  isName,
  isNotPoBox,
  isAddress,
  isCity,
  isZipCode,
  isNotRestrictedState,
} from 'utils/validations';
import useNavigationHelpers from 'hooks/useNavigationHelpers';
import catchOnly from 'utils/catchOnly';
import { NonClientError } from 'hooks/requests/fetchUtils';
import { useSnackbar } from 'notistack';
import useUnifiedBuyer, {
  UnifiedBuyerError,
  UnifiedBuyerParams,
} from 'hooks/requests/unifiedBuyer';
import useLocateAndAttribute from 'hooks/useLocateAndAttribute';
import useGeolocation from 'hooks/useGeolocation';

type PropTypes = RouteComponentProps;

export interface FormValues {
  firstName: string | null;
  lastName: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

const fields = [
  'firstName',
  'lastName',
  'address1',
  'address2',
  'city',
  'state',
  'zip',
];

const BillingAddress: React.FC<PropTypes> = () => {
  const [shouldGeolocate] = useGeolocation();
  const application = useSelector(selectCreditApplication);
  const tenant = useSelector(selectTenant);
  const t = useTranslate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [unifiedBuyer] = useUnifiedBuyer();

  const { push } = useNavigationHelpers();

  const locateAndAttribute = useLocateAndAttribute();

  const initialValues: FormValues = pick(fields, application);

  const schema = useMemo(() => {
    return Yup.object().shape({
      firstName: Yup.string()
        .label(t('formLabel.firstName'))
        .nullable()
        .required()
        .max(15)
        .concat(isName),
      lastName: Yup.string()
        .label(t('formLabel.lastName'))
        .nullable()
        .required()
        .max(20)
        .concat(isName),
      address1: Yup.string()
        .label(t('formLabel.address1'))
        .max(30)
        .nullable()
        .required()
        .concat(isAddress)
        .concat(isNotPoBox),
      address2: Yup.string()
        .label(t('formLabel.address2'))
        .max(30)
        .nullable()
        .concat(isAddress)
        .concat(isNotPoBox),
      city: Yup.string()
        .label(t('formLabel.city'))
        .nullable()
        .concat(isCity)
        .required(),
      state: Yup.string()
        .label(t('formLabel.state'))
        .nullable()
        .required()
        .concat(isNotRestrictedState(tenant, t('formErrors.restrictedStates'))),
      zip: Yup.string()
        .label(t('formLabel.zip'))
        .nullable()
        .required()
        .concat(isZipCode),
    });
  }, [t, tenant]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const castValues = schema.cast(values);

      try {
        const { emailAddress, mobilePhone, dateOfBirth, ssn } = application;
        dispatch(mergeApplication(castValues));

        await locateAndAttribute(castValues);

        const params = {
          ...castValues,
          emailAddress,
          mobilePhone,
          dateOfBirth,
          ssn,
        } as UnifiedBuyerParams;

        await unifiedBuyer(params);

        push('/unified/offer-application/eligibility');
      } catch (e) {
        if (catchOnly(e, NonClientError, UnifiedBuyerError)) {
          enqueueSnackbar('Something has gone wrong. Please try again later.', {
            variant: 'error',
            persist: true,
          });
        }
      }
    },
    [
      schema,
      application,
      dispatch,
      locateAndAttribute,
      unifiedBuyer,
      push,
      enqueueSnackbar,
    ],
  );

  return (
    <FmcForm
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      {formProps => (
        <BillingAddressView {...formProps} shouldGeolocate={shouldGeolocate} />
      )}
    </FmcForm>
  );
};

export default BillingAddress;
